import { Code, Group, Table } from '@mantine/core'
import { FormattedMessage } from 'react-intl'
import { OnlineStatus } from '@/components/devices/OnlineStatus/OnlineStatus'
import { CopyableText } from '@/components/ui-shared/CopyableText/CopyableText'
import { Camera } from '@/types/camera'
import { CameraSourceBadge } from '../CameraBadges/CameraSourceBadge'
import { CameraTypeBadge } from '../CameraBadges/CameraTypeBadge'

// Note: We need to support the old string format (e.g., "400x300") for backward compatibility.
// New cameras have an object format for resolution.
const formatResolution = (resolution: Camera['resolution']): string => {
  if (
    typeof resolution === 'object' &&
    resolution !== null &&
    'width' in resolution &&
    'height' in resolution
  ) {
    return `${resolution.width}x${resolution.height}`
  }

  return resolution
}

type CameraInfoProps = {
  camera: Camera
}

export const CameraInfo = ({ camera }: CameraInfoProps) => {
  return (
    <Table
      variant="vertical"
      layout="fixed"
      verticalSpacing="sm"
      horizontalSpacing="md"
      withTableBorder
    >
      <Table.Tbody>
        <Table.Tr>
          <Table.Th w={240}>
            <FormattedMessage id="cameras.fields.name" />
          </Table.Th>

          <Table.Td>{camera.camera_name}</Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="cameras.fields.id" />
          </Table.Th>

          <Table.Td>
            <CopyableText text={camera.camera_id}>
              <Code>{camera.camera_id}</Code>
            </CopyableText>
          </Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="cameras.fields.type" />
          </Table.Th>

          <Table.Td>
            <Group gap={4}>
              <CameraTypeBadge type={camera.stream_type} />
              <CameraSourceBadge source={camera.source} />
            </Group>
          </Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="cameras.fields.status" />
          </Table.Th>

          <Table.Td>
            <OnlineStatus status={camera.connected ? 'online' : 'offline'} />
          </Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="cameras.fields.fps" />
          </Table.Th>

          <Table.Td>{camera.fps}</Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="cameras.fields.resolution" />
          </Table.Th>

          <Table.Td>{formatResolution(camera.resolution)}</Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  )
}
